/* =====================================================================
   AstaLab 2.0 — catalogSnapshotEngine.js (round alpha.2, obiettivi 2, 7, 8)

   Logica NUOVA (non un porting: la 1.3 non ha un concetto di
   "catalogo cloud corrente/rimosso" — nella 1.3 tutti i giocatori sono
   sempre presenti in un'unica lista locale). Implementa esattamente le
   regole numerate della sezione 2 della richiesta del round alpha.2:
   il listone appena caricato rappresenta l'INTERO catalogo Fantacalcio
   corrente, riconciliato tramite fantacalcioId (mai un DELETE totale +
   INSERT cieco):
     - Id già presente  → aggiorna i campi anagrafici/quotazione,
       current=true, removedAt=null, conserva TUTTO il resto
       (id, statistiche, associazioni esterne, riferimenti storici);
     - Id nuovo         → crea il giocatore, current=true;
     - Id assente dal nuovo listone ma presente nel vecchio snapshot
       (e ancora current=true) → current=false, removedAt valorizzato,
       MAI cancellato (giocatore, statistiche e riferimenti restano
       intatti — vedi tests/test_catalog_replacement.js).

   Obiettivo 8 ("auto-riconoscimento storico per nuovi giocatori"): non
   richiede alcun passaggio di "collegamento" esplicito — statisticsEngine.
   getPlayerSeasonStats() cerca SEMPRE per fantacalcioId dentro
   statisticsArchives (che non e' mai indicizzato per player.id), quindi
   un nuovo giocatore trova automaticamente le proprie statistiche
   storiche non appena esiste nel catalogo, per costruzione — nessuna
   riga "in sospeso" da promuovere in un secondo momento. Questo modulo
   si limita a RIPORTARE, nel riepilogo restituito, quanti dei nuovi
   giocatori hanno già storico disponibile — un dato informativo per la
   UI/i test, non uno stato persistito.
   ===================================================================== */
import { uid, nowISO } from "./normalizeUtils.js";
import { buildFantacalcioIdIndex, getPlayerSeasonStats } from "./statisticsEngine.js";

/** Fabbrica di un giocatore-catalogo minimo, coerente con lo schema `players` (obiettivo 3). */
export function createCatalogPlayer(over){
  const now = nowISO();
  return Object.assign({
    id: uid("player"),
    fantacalcioId: null,
    nome: "",
    squadra: "",
    ruolo: "CEN",
    ruoloMantra: "",
    quotazione: 0,
    fvm: 0,
    current: true,
    removedAt: null,
    historicalAliases: [],
    externalIds: {},
    createdAt: now,
    updatedAt: now
  }, over||{});
}

/**
 * Riconcilia il catalogo corrente con un nuovo snapshot listone (obiettivo 2).
 * Muta `catalogState.players` (sostituendo l'array con uno nuovo — mai un mutate-in-place dei
 * singoli oggetti player esistenti, cosi' un chiamante che tiene ancora un riferimento al vecchio
 * oggetto non vede mai uno stato a metà). Non tocca MAI statisticsArchives/externalStatistics: la
 * riconciliazione dei record esterni in coda resta un passo SEPARATO, eseguito dal chiamante subito
 * dopo (vedi CatalogRepository — stessa "transazione" concettuale, motori distinti — obiettivo 11).
 *
 * @param {Object} catalogState - { players, statisticsArchives, currentSeasonKey, ... }
 * @param {Array} records - righe già normalizzate da parseQuotazioniRows()
 * @param {string} sourceFile
 * @param {{seasonKey:string,label:string}|null} seasonInfo
 */
export function reconcileCatalogSnapshot(catalogState, records, sourceFile, seasonInfo){
  if(seasonInfo && seasonInfo.seasonKey) catalogState.currentSeasonKey = seasonInfo.seasonKey;
  const now = nowISO();
  const existingPlayers = catalogState.players || [];
  const byId = buildFantacalcioIdIndex(existingPlayers);
  const incomingIds = new Set();

  const nextPlayers = existingPlayers.slice();
  const idToIndex = new Map(nextPlayers.map((p,i)=>[p.id,i]));

  let nuovi = 0, aggiornati = 0, invariati = 0;
  const nuoviGiocatori = [], aggiornatiGiocatori = [];

  records.forEach(rec=>{
    incomingIds.add(rec.fantacalcioId);
    const existing = byId.get(rec.fantacalcioId);
    if(existing){
      const fieldsChanged = existing.nome!==rec.nome || existing.squadra!==rec.squadra ||
        existing.ruolo!==rec.ruolo || (rec.ruoloMantra && existing.ruoloMantra!==rec.ruoloMantra) ||
        existing.quotazione!==rec.qtA || existing.fvm!==rec.fvm;
      const wasRemoved = existing.current!==true || existing.removedAt!=null;
      const updated = Object.assign({}, existing, {
        nome: rec.nome,
        squadra: rec.squadra,
        ruolo: rec.ruolo,
        ruoloMantra: rec.ruoloMantra || existing.ruoloMantra,
        quotazione: rec.qtA,
        fvm: rec.fvm,
        current: true,
        removedAt: null,
        updatedAt: now
      });
      nextPlayers[idToIndex.get(existing.id)] = updated;
      if(fieldsChanged || wasRemoved){ aggiornati++; aggiornatiGiocatori.push(updated); }
      else invariati++;
    } else {
      const created = createCatalogPlayer({
        fantacalcioId: rec.fantacalcioId, nome: rec.nome, squadra: rec.squadra,
        ruolo: rec.ruolo, ruoloMantra: rec.ruoloMantra||"", quotazione: rec.qtA, fvm: rec.fvm,
        current: true, createdAt: now, updatedAt: now
      });
      nextPlayers.push(created);
      idToIndex.set(created.id, nextPlayers.length-1);
      nuovi++;
      nuoviGiocatori.push(created);
    }
  });

  let rimossi = 0;
  const removedPlayers = [];
  for(let idx=0; idx<nextPlayers.length; idx++){
    const p = nextPlayers[idx];
    if(p.current===true && p.fantacalcioId!=null && !incomingIds.has(p.fantacalcioId)){
      const updated = Object.assign({}, p, { current:false, removedAt: now, updatedAt: now });
      nextPlayers[idx] = updated;
      rimossi++;
      removedPlayers.push({id:updated.id, nome:updated.nome, squadra:updated.squadra, fantacalcioId:updated.fantacalcioId});
    }
  }

  catalogState.players = nextPlayers;

  // Obiettivo 8: puramente informativo (vedi commento in cima al file) — nessuno stato scritto qui.
  const seasonsWithArchives = Object.keys(catalogState.statisticsArchives||{});
  const autoLinked = nuoviGiocatori.map(p=>{
    const linkedSeasons = seasonsWithArchives.filter(sk => !!getPlayerSeasonStats(p, sk, catalogState));
    return { playerId:p.id, fantacalcioId:p.fantacalcioId, nome:p.nome, linkedSeasons, missingSeasons: seasonsWithArchives.filter(sk=>!linkedSeasons.includes(sk)) };
  });

  return {
    nuovi, aggiornati, invariati, rimossi,
    nuoviPlayerIds: nuoviGiocatori.map(p=>p.id),
    aggiornatiPlayerIds: aggiornatiGiocatori.map(p=>p.id),
    removedPlayers,
    sourceFile,
    autoLinked
  };
}

/** Elenco dei soli giocatori acquistabili (obiettivo 2: "Il catalogo acquistabile deve usare
 *  soltanto current=true"). */
export function listCurrentPlayers(catalogState){
  return (catalogState.players||[]).filter(p=>p.current===true);
}
